// frontend/src/pages/NotificationsPage.jsx

import React, { useState, useEffect } from 'react';
import { Bell, BellOff, ArrowRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // بارگذاری از API
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/notifications/notifications/', {
        params: { limit: 50 }
      });
      
      const data = response.data.results || response.data || [];
      const formattedData = data.map(item => ({
        id: item.id,
        title: item.title,
        message: item.message,
        type: { name: item.type_name || 'سیستمی' },
        priority: item.priority || 'medium',
        is_read: item.is_read || false,
        created_at: item.created_at,
        link: item.link || '#'
      }));
      
      setNotifications(formattedData);
      localStorage.setItem('mock_notifications', JSON.stringify(formattedData));
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('خطا در دریافت اعلان‌ها');
      
      // Fallback به localStorage
      const saved = localStorage.getItem('mock_notifications');
      if (saved) {
        try {
          setNotifications(JSON.parse(saved));
        } catch (e) {}
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/notifications/mark_all_read/');
      const updated = notifications.map(n => ({ ...n, is_read: true }));
      setNotifications(updated);
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
    } catch (err) {
      console.error('Error marking all as read:', err);
      // Fallback محلی
      const updated = notifications.map(n => ({ ...n, is_read: true }));
      setNotifications(updated);
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.post(`/notifications/notifications/${id}/mark_read/`);
      const updated = notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      );
      setNotifications(updated);
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
    } catch (err) {
      console.error('Error marking as read:', err);
      // Fallback محلی
      const updated = notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      );
      setNotifications(updated);
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/notifications/notifications/${id}/`);
      const updated = notifications.filter(n => n.id !== id);
      setNotifications(updated);
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
    } catch (err) {
      console.error('Error deleting notification:', err);
      // Fallback محلی
      const updated = notifications.filter(n => n.id !== id);
      setNotifications(updated);
      localStorage.setItem('mock_notifications', JSON.stringify(updated));
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = (now - new Date(date)) / 1000;
    if (diff < 60) return 'لحظاتی پیش';
    if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه پیش`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت پیش`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} روز پیش`;
    return new Date(date).toLocaleDateString('fa-IR');
  };

  const getUnreadCount = () => {
    return notifications.filter(n => !n.is_read).length;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Bell className="w-7 h-7 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">همه اعلان‌ها</h1>
              {getUnreadCount() > 0 && (
                <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                  {getUnreadCount()} نخوانده
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {getUnreadCount() > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  خواندن همه
                </button>
              )}
              <Link
                to="/"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
              >
                <ArrowRight className="w-4 h-4" />
                بازگشت
              </Link>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {notifications.length === 0 ? (
            <div className="text-center text-gray-400 py-16">
              <BellOff className="w-20 h-20 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">هیچ اعلانی وجود ندارد</p>
              <p className="text-sm mt-1">با ثبت سفارش یا فعالیت جدید، اعلان‌ها اینجا نمایش داده می‌شوند</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border transition-all ${
                    !notification.is_read 
                      ? 'bg-blue-50/80 border-blue-200 shadow-sm' 
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${
                      !notification.is_read 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      <Bell className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-medium ${
                          !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <span className="flex-shrink-0 w-2.5 h-2.5 mt-1.5 bg-blue-600 rounded-full animate-pulse"></span>
                        )}
                      </div>
                      <p className="text-gray-600 mt-1 leading-relaxed">
                        {notification.message}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <span className="text-xs text-gray-400">
                          {formatTime(notification.created_at)}
                        </span>
                        {notification.type?.name && (
                          <span className="text-xs px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                            {notification.type.name}
                          </span>
                        )}
                        <div className="flex items-center gap-2 mr-auto">
                          {!notification.is_read && (
                            <button
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors px-2 py-1 hover:bg-blue-50 rounded"
                            >
                              ✓ خوانده شد
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(notification.id)}
                            className="text-xs text-gray-400 hover:text-red-500 transition-colors px-2 py-1 hover:bg-red-50 rounded"
                          >
                            ✕ حذف
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {notifications.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center text-sm text-gray-500">
              <span>تعداد کل: {notifications.length}</span>
              <span>خوانده شده: {notifications.filter(n => n.is_read).length}</span>
              <span>نخوانده: {getUnreadCount()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPage;